// AccessKeyModal.tsx
import React, { useEffect, useRef, useState } from 'react';

import { SafetyOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import {
  Button,
  ModalActions,
  ModalBox,
  ModalHeader,
  ModalOverlay,
  CloseX,
  KeyInput,
  ErrorText,
  ContentIcon,
} from './styled';

import { useTheme } from '../../../hooks/useTheme';
import {
  ensureLoginFromEnv,
  getKeyByParams,
} from '../../../services/licenseService';
import { Shell } from '../../atoms/Shell/Shell';

type VersionType = 'cve-lite' | 'cve-pro';

type Props = {
  open: boolean;
  onClose: () => void;
  // agora retorna também a versão escolhida
  onSuccess: (key: string, version: VersionType) => void;
};

const KEY_REGEX = /^CVE-[A-Za-z0-9-_.]{8,}$/;

export default function AccessKeyModal({ open, onSuccess }: Props) {
  const [key, setKey] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  // versão selecionada (default CVE-Pro)
  const [version, setVersion] = useState<VersionType>('cve-pro');

  const inputRef = useRef<HTMLInputElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
      setErr('');
      setKey('');
      setLoading(false);
      setVersion('cve-pro'); // sempre resetar para default
    }
  }, [open]);

  if (!open) return null;

  async function verify() {
    setErr('');
    if (!key || !KEY_REGEX.test(key)) {
      setErr('Chave inválida. Digite novamente.');
      return;
    }

    try {
      setLoading(true);

      // 1) login na API usando credenciais do .env
      await ensureLoginFromEnv();

      // 2) buscar a chave
      const { data } = await getKeyByParams(key);

      const license = Array.isArray(data?.items)
        ? data.items[0]
        : Array.isArray(data)
        ? data[0]
        : data;

      if (!license) {
        setErr('Licença não encontrada.');
        return;
      }

      const status = String(
        license.status || license?.Status || '',
      ).toLowerCase();

      if (!status) {
        setErr('Licença não encontrada!');
        return;
      }

      if (status === 'ativada' || status === 'active') {
        setErr('Esta chave já está em utilização!');
        return;
      }

      if (status === 'expirada' || status === 'expired') {
        setErr('Esta chave está expirada!');
        return;
      }

      if (status !== 'criada' && status !== 'created') {
        setErr(`Licença inválida: status "${status}".`);
        return;
      }

      localStorage.setItem('chave-key', key);

      // devolve chave + versão escolhida
      onSuccess(key, version);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Falha ao validar a licença.';
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !loading) verify();
  }

  return (
    <ModalOverlay role='dialog' aria-modal='true' aria-labelledby='modal-title'>
      <ModalBox>
        <CloseX
          as={Link}
          to='/auth/login'
          aria-label='Voltar para a página inicial'
        >
          ×
        </CloseX>

        <ModalHeader>
          {theme ? (
            <ContentIcon>
              <SafetyOutlined style={{ fontSize: 36 }} />
            </ContentIcon>
          ) : (
            <Shell height={22} width={200} />
          )}
          <h3 id='modal-title'>Insira a chave de acesso</h3>
        </ModalHeader>

        <KeyInput
          ref={inputRef}
          placeholder='CVE-xxxxxxxxxxxxxxxx'
          value={key}
          onChange={(e) => setKey(e.target.value.trim())}
          onKeyDown={handleKey}
          autoComplete='off'
          disabled={loading}
        />

        {/* Radios de escolha da versão */}

        <h3 style={{ marginTop: '2rem' }}>Escolha a versão:</h3>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type='radio'
              name='version'
              value='cve-pro'
              checked={version === 'cve-pro'}
              onChange={() => setVersion('cve-pro')}
              disabled={loading}
            />
            CVE-Pro (versão paga)
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type='radio'
              name='version'
              value='cve-lite'
              checked={version === 'cve-lite'}
              onChange={() => setVersion('cve-lite')}
              disabled={loading}
            />
            CVE-Lite (versão gratuita)
          </label>
        </div>

        {err && <ErrorText>{err}</ErrorText>}

        <ModalActions>
          {theme ? (
            <Button onClick={verify} disabled={loading}>
              {loading ? 'Verificando...' : 'Verificar'}
            </Button>
          ) : (
            <Shell height={22} width={200} />
          )}
        </ModalActions>
      </ModalBox>
    </ModalOverlay>
  );
}
